import type { FC, FormEvent, ReactNode } from 'react'
import type { ShellData } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { ChevronDown, Globe, Menu, MessageCircle, Search, ShoppingBag, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { useCart } from '@/lib/cart'
import { whatsappLink } from '@/lib/phone'
import { Logo } from '@/components/store/logo'
import { CmsText } from '@/components/store/cms-text'
import { categoryIcon } from '@/components/store/product-media'
import { Drawer } from '@/components/ui/overlay'

interface SiteHeaderProps {
    shell: ShellData
}

export const SiteHeader: FC<SiteHeaderProps> = ({ shell }): ReactNode => {
    const { tx, locale } = useI18n()
    const router = useRouter()
    const { lineCount, open: openCart } = useCart()
    const [menuOpen, setMenuOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [query, setQuery] = useState('')

    const { settings, categories } = shell
    const otherLocale = locale === 'en' ? 'ar' : 'en'

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 8)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = (): void => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        const current = router.query.q

        setQuery(typeof current === 'string' ? current : '')
    }, [router.query.q])

    const nav = [
        { href: '/products', label: tx('كل المنتجات', 'All products') },
        { href: '/offers', label: tx('العروض', 'Offers') },
        { href: '/shipping', label: tx('الشحن والتوصيل', 'Shipping') },
        { href: '/track', label: tx('تتبع طلبك', 'Track order') },
        { href: '/contact', label: tx('تواصل معنا', 'Contact') }
    ]

    const isActive = (href: string): boolean => router.pathname === href || router.pathname.startsWith(`${href}/`)

    const submitSearch = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const term = query.trim()

        void router.push({ pathname: '/products', query: term ? { q: term } : {} })
    }

    const searchForm = (id: string, className?: string): ReactNode => (
        <form role='search' onSubmit={submitSearch} className={cn('relative', className)}>
            <label htmlFor={id} className='sr-only'>{tx('ابحث عن منتج', 'Search products')}</label>
            <Search className='pointer-events-none absolute start-3.5 top-1/2 size-4 -translate-y-1/2 text-navy-400' aria-hidden='true' />
            <input
                id={id}
                type='search'
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder={tx('ابحث عن منتج...', 'Search products...')}
                className='h-10 w-full rounded-full border border-navy-100 bg-navy-50/70 ps-10 pe-4 text-sm text-navy-950 transition-colors placeholder:text-navy-400 hover:border-navy-200 focus:border-cyan-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-cyan-400/20'
            />
        </form>
    )

    return (
        <>
            <a href='#main' className='sr-only focus:not-sr-only focus:fixed focus:start-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-navy-900 focus:px-4 focus:py-2 focus:text-white'>
                {tx('انتقل إلى المحتوى', 'Skip to content')}
            </a>

            {shell.announcement && (
                <div className='bg-navy-950 text-white'>
                    <div className='container-page flex min-h-9 items-center justify-center gap-2 py-2 text-center text-xs sm:text-[13px]'>
                        <Sparkles className='size-3.5 shrink-0 text-cyan-300' aria-hidden='true' />
                        <CmsText table='homepage_content' id={shell.announcement.id} field={shell.announcement.text} className='leading-relaxed text-white/90' />
                    </div>
                </div>
            )}

            <header className={cn('sticky top-0 z-50 border-b bg-white/85 backdrop-blur-xl transition-shadow duration-300', scrolled ? 'border-navy-100 shadow-[0_8px_30px_-18px_rgb(8_18_41/0.35)]' : 'border-transparent')}>
                <div className='container-page flex h-16 items-center gap-3 lg:h-[72px] lg:gap-6'>
                    <button
                        type='button'
                        onClick={() => setMenuOpen(true)}
                        className='-ms-2 inline-flex size-10 items-center justify-center rounded-full text-navy-900 transition-colors hover:bg-navy-50 lg:hidden'
                        aria-label={tx('القائمة', 'Menu')}
                    >
                        <Menu className='size-5' />
                    </button>

                    <Logo name={settings.storeName} settingsId={settings.id} />

                    <nav className='ms-2 hidden items-center gap-1 lg:flex' aria-label={tx('القائمة الرئيسية', 'Main navigation')}>
                        <div className='group relative'>
                            <button type='button' className='inline-flex h-10 items-center gap-1 rounded-full px-3.5 text-[15px] font-medium text-navy-700 transition-colors hover:bg-navy-50 hover:text-navy-950 group-focus-within:bg-navy-50' aria-haspopup='true'>
                                {tx('الأقسام', 'Categories')}
                                <ChevronDown className='size-4 transition-transform duration-200 group-hover:rotate-180 group-focus-within:rotate-180' aria-hidden='true' />
                            </button>

                            <div className='invisible absolute start-0 top-full z-50 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100'>
                                <div className='grid w-[520px] grid-cols-2 gap-1 rounded-3xl border border-navy-100 bg-white p-3 shadow-lift'>
                                    {categories.map(category => {
                                        const Icon = categoryIcon(category.slug)

                                        return (
                                            <Link key={category.id} href={`/categories/${category.slug}`} className='flex items-center gap-3 rounded-2xl p-3 transition-colors hover:bg-navy-50'>
                                                <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-50 to-cyan-50 text-navy-800 ring-1 ring-navy-100'>
                                                    <Icon className='size-5' strokeWidth={1.6} aria-hidden='true' />
                                                </span>
                                                <CmsText table='categories' id={category.id} field={category.name} className='text-sm font-medium text-navy-900' />
                                            </Link>
                                        )
                                    })}

                                    <Link href='/products' className='col-span-2 mt-1 flex items-center justify-center rounded-2xl bg-navy-50 p-3 text-sm font-semibold text-navy-900 transition-colors hover:bg-navy-100'>
                                        {tx('تصفح كل المنتجات', 'Browse all products')}
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {nav.slice(0, 3).map(item => (
                            <Link
                                key={item.href}
                                href={item.href}
                                className={cn('inline-flex h-10 items-center rounded-full px-3.5 text-[15px] font-medium transition-colors hover:bg-navy-50 hover:text-navy-950', isActive(item.href) ? 'bg-navy-50 text-navy-950' : 'text-navy-700')}
                                aria-current={isActive(item.href) ? 'page' : undefined}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    <div className='ms-auto flex items-center gap-1.5 sm:gap-2'>
                        {searchForm('header-search', 'hidden w-56 md:block xl:w-72')}

                        <Link
                            href='/products'
                            className='inline-flex size-10 items-center justify-center rounded-full text-navy-900 transition-colors hover:bg-navy-50 md:hidden'
                            aria-label={tx('ابحث عن منتج', 'Search products')}
                        >
                            <Search className='size-5' />
                        </Link>

                        <Link
                            href={router.asPath}
                            locale={otherLocale}
                            className='hidden h-10 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-navy-700 transition-colors hover:bg-navy-50 hover:text-navy-950 sm:inline-flex'
                            hrefLang={otherLocale}
                        >
                            <Globe className='size-4' aria-hidden='true' />
                            {locale === 'en' ? 'العربية' : 'English'}
                        </Link>

                        <button
                            type='button'
                            onClick={openCart}
                            className='relative inline-flex h-10 items-center gap-2 rounded-full bg-navy-900 ps-3.5 pe-4 text-sm font-medium text-white shadow-sm transition-all hover:bg-navy-800 hover:shadow-md active:scale-[0.98]'
                            aria-label={tx('سلة التسوق', 'Shopping cart')}
                        >
                            <ShoppingBag className='size-4.5' aria-hidden='true' />
                            <span className='hidden sm:inline'>{tx('السلة', 'Cart')}</span>
                            <span className={cn('inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums transition-colors', lineCount > 0 ? 'bg-cyan-400 text-navy-950' : 'bg-white/15 text-white/80')}>
                                {lineCount}
                            </span>
                        </button>
                    </div>
                </div>
            </header>

            <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} side='start' title={tx('القائمة', 'Menu')} className='max-w-sm'>
                <div className='flex flex-col gap-6 p-5'>
                    {searchForm('drawer-search')}

                    <nav className='flex flex-col' aria-label={tx('القائمة', 'Menu')}>
                        {nav.map(item => (
                            <Link key={item.href} href={item.href} className={cn('rounded-xl px-3 py-3 text-base font-medium transition-colors hover:bg-navy-50', isActive(item.href) ? 'bg-navy-50 text-navy-950' : 'text-navy-800')}>
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    {categories.length > 0 && (
                        <div className='flex flex-col gap-2'>
                            <p className='px-3 text-xs font-semibold text-navy-400'>{tx('الأقسام', 'Categories')}</p>

                            <div className='grid grid-cols-2 gap-2'>
                                {categories.map(category => {
                                    const Icon = categoryIcon(category.slug)

                                    return (
                                        <Link key={category.id} href={`/categories/${category.slug}`} className='flex flex-col gap-2 rounded-2xl border border-navy-100 p-3 transition-colors hover:border-navy-200 hover:bg-navy-50'>
                                            <Icon className='size-5 text-navy-700' strokeWidth={1.6} aria-hidden='true' />
                                            <CmsText table='categories' id={category.id} field={category.name} className='text-sm font-medium leading-snug text-navy-900' />
                                        </Link>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    <div className='flex flex-col gap-2 border-t border-border pt-5'>
                        <Link href={router.asPath} locale={otherLocale} hrefLang={otherLocale} className='flex items-center gap-3 rounded-xl px-3 py-3 text-base font-medium text-navy-800 transition-colors hover:bg-navy-50'>
                            <Globe className='size-5 text-navy-500' aria-hidden='true' />
                            {locale === 'en' ? 'العربية' : 'English'}
                        </Link>

                        {settings.whatsapp && (
                            <a href={whatsappLink(settings.whatsapp)} target='_blank' rel='noopener noreferrer' className='flex items-center gap-3 rounded-xl px-3 py-3 text-base font-medium text-navy-800 transition-colors hover:bg-navy-50'>
                                <MessageCircle className='size-5 text-[#1fa855]' aria-hidden='true' />
                                {tx('راسلنا على واتساب', 'Message us on WhatsApp')}
                            </a>
                        )}
                    </div>
                </div>
            </Drawer>
        </>
    )
}
